import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell } from '@phosphor-icons/react';

import { Checkbox } from '@/components/ui/checkbox';
import { FormSkeleton } from '@/components/ui/form-skeleton';
import { useToast } from '@/components/Toast';
import {
  listNotificationPreferences,
  updateNotificationPreference,
  type NotificationPreference,
} from '@/api/notificationPreferences';

// Notifications — per-user notification channel preferences. One row per known
// kind with an email + push toggle each. Cross-org (the choices follow the user)
// so it lives under /me. Email defaults OFF (opt-in); push defaults ON.
export function Notifications() {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const token = user?.access_token ?? null;
  const toast = useToast();
  const qc = useQueryClient();

  const { data: prefs, isLoading } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: () => listNotificationPreferences(token),
  });

  const mutation = useMutation({
    mutationFn: (input: NotificationPreference) =>
      updateNotificationPreference(token, input.kind, {
        email_enabled: input.email_enabled,
        push_enabled: input.push_enabled,
      }),
    onSuccess: (updated) => {
      qc.setQueryData<NotificationPreference[]>(['notification-preferences'], (prev) =>
        (prev ?? []).map((p) => (p.kind === updated.kind ? updated : p)),
      );
      toast.success(t('settings.notifications.saved'));
    },
    onError: () => {
      // Roll back to server truth so a failed toggle doesn't stick visually.
      void qc.invalidateQueries({ queryKey: ['notification-preferences'] });
      toast.error(t('settings.notifications.saveFailed'));
    },
  });

  function setChannel(pref: NotificationPreference, channel: 'email' | 'push', enabled: boolean) {
    mutation.mutate({
      ...pref,
      ...(channel === 'email' ? { email_enabled: enabled } : { push_enabled: enabled }),
    });
  }

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 p-4 md:p-8">
      <header className="flex flex-col gap-1">
        <p className="font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('settings.title')}
        </p>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight md:text-4xl">
          {t('settings.notifications.title')}
        </h1>
        <p className="text-muted-foreground">{t('settings.notifications.subtitle')}</p>
      </header>

      <div className="flex flex-col gap-6 rounded-2xl border border-border bg-card p-6 shadow-sm">
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill bg-muted text-foreground/80"
          >
            <Bell className="h-5 w-5" weight="bold" />
          </span>
          <div className="flex flex-col gap-1">
            <h2 className="font-heading text-lg font-bold">
              {t('settings.notifications.channels.title')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('settings.notifications.channels.body')}
            </p>
          </div>
        </div>

        {isLoading ? (
          <FormSkeleton rows={4} action={false} bare />
        ) : !prefs || prefs.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('settings.notifications.empty')}</p>
        ) : (
          <ul className="flex flex-col divide-y divide-border">
            {prefs.map((pref) => (
              <li
                key={pref.kind}
                className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex flex-col gap-0.5">
                  <span className="font-heading text-sm font-semibold">
                    {t(`settings.notifications.kind.${pref.kind}.label`)}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {t(`settings.notifications.kind.${pref.kind}.help`)}
                  </span>
                </div>
                <div className="flex shrink-0 gap-5">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={pref.push_enabled}
                      disabled={mutation.isPending}
                      onChange={(e) => setChannel(pref, 'push', e.target.checked)}
                    />
                    {t('settings.notifications.channel.push')}
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={pref.email_enabled}
                      disabled={mutation.isPending}
                      onChange={(e) => setChannel(pref, 'email', e.target.checked)}
                    />
                    {t('settings.notifications.channel.email')}
                  </label>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
